import React, {useState} from 'react';
import StatList from "components/StatList";
import StatBoxSimple from "components/StatBoxSimple";
import Divider from "components/Divider";
import StackedList from "components/StackedList";
import StackedListItem from "components/StackedListItem";
import Link from "components/Link";
import DotBadge from "components/DotBadge";
import {formattedDate} from "services/date";
import Card from "components/Card";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {UserInsights} from "types/User";
import MembershipStatus from "types/MembershipStatus";
import TicketStatus from "../../../types/TicketStatus";
import Selector from "../../Form/Selector";
import {Item} from "../../Form/Selector/Selector";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

type TicketsCardProps = {
  userId: number
  insights?: UserInsights | null
}

// Helpers ----

const statusColors: Record<MembershipStatus, number> = {
  [MembershipStatus.UNPAID]: DotBadge.colors.YELLOW,
  [MembershipStatus.PAID]: DotBadge.colors.BLUE,
  [MembershipStatus.VALID]: DotBadge.colors.GREEN,
  [MembershipStatus.EXPIRED]: DotBadge.colors.GRAY,
  [MembershipStatus.REVOKED]: DotBadge.colors.RED,
}

const statusOptions: Item[] = [
  {id: TicketStatus.VALID, name: 'Valid'},
  {id: TicketStatus.EXPIRED, name: 'Expired'},
  {id: TicketStatus.PAID, name: 'Paid'},
  {id: TicketStatus.UNPAID, name: 'Unpaid'},
  {id: TicketStatus.REVOKED, name: 'Revoked'},
]

// Component ----

function TicketsCard({userId, insights}: TicketsCardProps) {
  const {termFor} = useOperator();

  const [ticketStatus, setTicketStatus] = useState<TicketStatus>(TicketStatus.VALID);

  const {data: tickets, isLoading: isLoadingTickets} = useQuery(
    ['tickets.all', {holder: userId, status: ticketStatus}],
    () => ngage.tickets.filtered({holder: userId, status: ticketStatus}),
    {keepPreviousData: false}
  )

  return (
    <Card heading="Tickets" isLoading={isLoadingTickets}>
      <StatList columns={2}>
        <StatBoxSimple name='Total' stat={tickets?.data.length ?? 0}/>
        <StatBoxSimple name='Spent' stat={insights?.engagement.ticket_spend_formatted}/>
      </StatList>

      <div className='max-w-[18ch] mt-7'>
        <Selector
          label='Status'
          items={statusOptions}
          selectedId={TicketStatus.VALID}
          onChange={item => setTicketStatus(item.id as TicketStatus)}
        />
      </div>

      {!tickets?.data?.length ? (
        <div className="mt-6 flow-root">
          <Divider/>
          <p className="text-sm text-gray-400 mt-6">{isLoadingTickets ? 'Loading' : 'No'} {termFor('tickets')}</p>
        </div>
      ) : (
        <div className="mt-6">
          <StackedList>
            {tickets?.data?.map((ticket) => (
              <StackedListItem hasLink={true} key={ticket.id}>
                <Link to={`/tickets/${ticket.id}`}>
                  <div className="flex items-center">
                    <p className="truncate text-sm font-medium text-gray-500">{ticket.type.name}</p>

                    <div className="ml-3 flex flex-shrink-0">
                      <DotBadge color={statusColors[ticket.status]} text={ticket.status}/>
                    </div>

                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-1 font-normal text-sm text-gray-900">
                    <p>
                      <span className="mr-1.5 text-gray-400">Number:</span>
                      {ticket.number}
                    </p>
                    <p>
                      <span className="mr-1.5 text-gray-400">Expires:</span>
                      {formattedDate(ticket.expires_at)}
                    </p>
                  </div>
                </Link>
              </StackedListItem>
            ))}
          </StackedList>
        </div>
      )}
    </Card>
  );
}

// Export ----

export default TicketsCard;